import { ChangeEvent, memo, MouseEvent, useCallback, useMemo, useState } from 'react';
import { Chip } from '@cbhq/cds-web/chips';
import { Box, Fallback, VStack } from '@cbhq/cds-web/layout';
import { HStack } from '@cbhq/cds-web/layout';
import { RemoteImage } from '@cbhq/cds-web/media';
import { TextHeadline, TextLabel1, TextLabel2 } from '@cbhq/cds-web/typography';
import { css, cx } from '@emotion/css';

import { PressableOpacity } from '@cbhq/cds-web/system';
import { useBreakpoints } from '@cbhq/cds-web/hooks/useBreakpoints';
import { CurrencyField } from '@/types';
import { useAddLiquidityState } from '@/components/AddLiquidity/AddLiquidityProvider';
import {
  isPositiveNumericInputValue,
  isValidNumericInputValue,
  parseNumericInputValue,
} from '@/components/AddLiquidity/utils';
import { format, DisplayType } from '@/utils/numberFormatter';
import { NESTED_CARD_BORDER_RADIUS } from '@/constants/layout';

type DepositAmountInputProps = {
  value: string;
  tokenSymbol: string;
  tokenImgUrl: string;
  balance: string;
  maxDecimalPrecision: number;
  onChange?: (value: string) => void;
  fallback?: boolean;
  disabled?: boolean;
  testID: string;
};

const InputStyles = css`
  font-weight: var(--fontWeight-body);
  font-family: var(--fontFamily-body);

  font-size: 28px;
  line-height: 36px;

  background: transparent;
  color: var(--color-fg);
  width: 100%;
  height: 36px;
  padding: 0;

  &::placeholder {
    color: rgb(var(--gray40));
  }

  // Hide default focus ring
  &:focus {
    outline: none;
  }
`;

const InputWrapperStyles = css`
  /* Fade out the right-most edge of input */
  mask-image: linear-gradient(to right, rgba(0, 0, 0, 1) 95%, rgba(0, 0, 0, 0) 100%);
  -webkit-mask-image: linear-gradient(to right, rgba(0, 0, 0, 1) 95%, rgba(0, 0, 0, 0) 100%);
`;

const WrapperStyles = css`
  box-shadow: 0 0 0 2px transparent;
  cursor: text;
  width: 100%;

  transition: box-shadow 75ms linear;
`;

const WrapperActiveStyles = css`
  border-color: var(--color-bgPrimary);
  box-shadow: 0 0 0 2px var(--color-bgPrimary);
`;

const WrapperErrorStyles = css`
  border-color: var(--color-bgNegative);
  box-shadow: 0 0 0 2px var(--color-bgNegative);
`;

const ChipContainerStyles = css`
  min-width: 80px;
  flex-shrink: 0;
`;

function DepositAmountInput({
  value,
  tokenSymbol,
  tokenImgUrl,
  balance,
  maxDecimalPrecision,
  onChange,
  fallback,
  disabled,
  testID,
}: DepositAmountInputProps) {
  const [focused, setFocused] = useState(false);
  const handleFocus = useCallback(() => setFocused(true), []);
  const handleBlur = useCallback(() => setFocused(false), []);

  const hasBalance = useMemo(() => isPositiveNumericInputValue(balance), [balance]);

  const insufficientBalance = useMemo(() => {
    const balanceValue = Number.parseFloat(balance ?? '');
    const valueValue = Number.parseFloat(value ?? '');
    return balanceValue < valueValue;
  }, [balance, value]);

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (onChange && isValidNumericInputValue(event.target.value, maxDecimalPrecision)) {
        const changeValue = parseNumericInputValue(event.target.value);

        onChange(changeValue);
      }
    },
    [maxDecimalPrecision, onChange],
  );

  const handleMaxPress = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      event.stopPropagation();
      onChange?.(balance);
    },
    [balance, onChange],
  );

  return (
    <VStack
      className={cx(
        WrapperStyles,
        focused && WrapperActiveStyles,
        insufficientBalance && WrapperErrorStyles,
      )}
      minWidth={240}
      width="calc(50% - 8px)"
      flexGrow={1}
      padding={2}
      bordered
      gap={1}
      borderRadius={NESTED_CARD_BORDER_RADIUS}
    >
      <HStack justifyContent="space-between" alignItems="center">
        <Box className={InputWrapperStyles} paddingBottom={0.5} opacity={disabled ? 0.5 : 1}>
          {!fallback && (
            <input
              className={InputStyles}
              type="text" // "text" lends the most flexibility for validation
              value={value ?? ''}
              placeholder="0.0"
              onChange={handleChange}
              onFocus={handleFocus}
              onBlur={handleBlur}
              disabled={disabled}
              data-testid={`${testID}-input`}
            />
          )}
          {fallback && <Fallback width={180} height={36} shape="squircle" />}
        </Box>
        {!fallback && (
          <Box className={ChipContainerStyles}>
            <Chip
              testID={`${testID}-asset-chip`}
              compact
              start={<RemoteImage source={tokenImgUrl} shape="circle" size="s" />}
            >
              {tokenSymbol}
            </Chip>
          </Box>
        )}
        {fallback && <Fallback width={70} height={32} shape="circle" />}
      </HStack>
      {!fallback && (
        <HStack alignItems="center" justifyContent="space-between" gap={0.5} flexWrap="wrap">
          <HStack alignItems="center" gap={1}>
            <TextLabel2 testID={`${testID}-balance-label`} as="p" color="fgMuted" noWrap>
              Balance:{' '}
              {hasBalance ? format({ value: balance, type: DisplayType.TokenAmount }) : '--'}
            </TextLabel2>
            {hasBalance && (
              <PressableOpacity
                testID={`${testID}-max-button`}
                onClick={handleMaxPress}
                disabled={disabled}
              >
                <TextLabel1 as="span" color="fgPrimary">
                  Max
                </TextLabel1>
              </PressableOpacity>
            )}
          </HStack>
          {insufficientBalance && (
            <TextLabel2 testID={`${testID}-error-message`} as="p" color="fgNegative" noWrap>
              Insufficient balance
            </TextLabel2>
          )}
        </HStack>
      )}
      {fallback && <Fallback width={100} height={20} shape="squircle" />}
    </VStack>
  );
}

export const AddLiquidityDepositInputs = memo(function AddLiquidityDepositInputs() {
  const {
    setDepositAmountState,
    depositAmountsInput,
    positionTokens,
    priceInputs,
    accountBalances,
    currencyDepositDisabled,
  } = useAddLiquidityState();

  const disabled = useMemo(() => {
    return (
      !isPositiveNumericInputValue(priceInputs.lower.value) ||
      !isPositiveNumericInputValue(priceInputs.upper.value)
    );
  }, [priceInputs.lower.value, priceInputs.upper.value]);

  const [isDeposit0Disabled, isDeposit1Disabled] = useMemo(() => {
    // assuming input0 is always for poolKey.currency0 and input1 is always for poolKey.currency1
    return [
      currencyDepositDisabled[CurrencyField.CURRENCY_0] || disabled,
      currencyDepositDisabled[CurrencyField.CURRENCY_1] || disabled,
    ];
  }, [currencyDepositDisabled, disabled]);

  const handleChangeDepositAmounts = useMemo(
    () => [
      (value: string) => {
        setDepositAmountState({
          amount: value,
          currency: CurrencyField.CURRENCY_0,
        });
      },
      (value: string) => {
        setDepositAmountState({
          amount: value,
          currency: CurrencyField.CURRENCY_1,
        });
      },
    ],
    [setDepositAmountState],
  );

  const { isPhone, isPhoneLandscape } = useBreakpoints();
  const showMobileLayout = useMemo(() => isPhone ?? isPhoneLandscape, [isPhone, isPhoneLandscape]);

  return (
    <VStack gap={1.5}>
      <HStack alignItems="center" flexWrap="wrap">
        <TextHeadline as="p" testID="deposit-amounts-title" noWrap>
          Set deposit amounts
        </TextHeadline>
      </HStack>

      <HStack gap={2} flexWrap={showMobileLayout ? 'wrap' : 'nowrap'}>
        {/* poolKey.currency0 */}
        <DepositAmountInput
          value={depositAmountsInput[0] ?? ''}
          tokenSymbol={positionTokens[0].symbol}
          tokenImgUrl={positionTokens[0].imageUrl}
          balance={accountBalances[positionTokens[0].asset.displaySymbol].formatted}
          maxDecimalPrecision={positionTokens[0].asset.exponent}
          onChange={handleChangeDepositAmounts[0]}
          disabled={isDeposit0Disabled}
          testID="liquidity-deposit-input-0"
        />

        {/* poolKey.currency1 */}
        <DepositAmountInput
          value={depositAmountsInput[1] ?? ''}
          tokenSymbol={positionTokens[1].symbol}
          tokenImgUrl={positionTokens[1].imageUrl}
          balance={accountBalances[positionTokens[1].asset.displaySymbol].formatted}
          maxDecimalPrecision={positionTokens[1].asset.exponent}
          onChange={handleChangeDepositAmounts[1]}
          disabled={isDeposit1Disabled}
          testID="liquidity-deposit-input-1"
        />
      </HStack>
    </VStack>
  );
});

export const AddLiquidityDepositInputsLoading = memo(function AddLiquidityDepositInputsLoading() {
  const { isPhone, isPhoneLandscape } = useBreakpoints();
  const showMobileLayout = useMemo(() => isPhone ?? isPhoneLandscape, [isPhone, isPhoneLandscape]);

  return (
    <VStack gap={1.5}>
      <HStack alignItems="center" flexWrap="wrap">
        <TextHeadline as="p" testID="deposit-amounts-title" noWrap>
          Set deposit amounts
        </TextHeadline>
      </HStack>

      <HStack gap={2} flexWrap={showMobileLayout ? 'wrap' : 'nowrap'}>
        <DepositAmountInput
          value=""
          tokenSymbol=""
          tokenImgUrl=""
          balance=""
          maxDecimalPrecision={0}
          testID="liquidity-deposit-fallback-0"
          fallback
        />

        <DepositAmountInput
          value=""
          tokenSymbol=""
          tokenImgUrl=""
          balance=""
          maxDecimalPrecision={0}
          testID="liquidity-deposit-fallback-1"
          fallback
        />
      </HStack>
    </VStack>
  );
});
